import React, { useCallback, useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import { Ban, RotateCcw, ChevronLeft, ChevronRight, UserPlus, Trash2, RefreshCw, Eye, Copy, Check, Wand2, BadgeCheck } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { saveMemberListCtx } from "@/lib/adminMemberNav";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Loading } from "@/components/common/Loading";
import { useAuth } from "@/context/AuthContext";
import { useRowSelection } from "@/hooks/useRowSelection";
import { BulkActionBar, runBulk } from "@/components/common/BulkActionBar";

import { AdminRefreshButton } from "@/components/admin/AdminRefreshButton";
import { TagPicker } from "@/components/tags/TagPicker";

const TIERS = [
  { value: "standard", label: "VIP" },
  { value: "pro", label: "PRO" },
  { value: "premium", label: "CAPITAL" },
  { value: "business", label: "TEAMS" },
];

const ROLES = [
  { value: "user", label: "Member" },
  { value: "moderator", label: "Moderator" },
  { value: "admin", label: "Admin" },
];

const SORTS = [
  { value: "recent", label: "Signup date — newest" },
  { value: "oldest", label: "Signup date — oldest" },
  { value: "name_asc", label: "Name A–Z" },
  { value: "name_desc", label: "Name Z–A" },
  { value: "tier", label: "Tier" },
  { value: "status", label: "Status" },
];

const EMPTY_FORM = { first_name: "", last_name: "", email: "", password: "", tier: "standard", role: "user", generate_password: false };

const fmtJoined = (s) => (s ? new Date(s).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "—");

export default function AdminUsers() {
  const { user: me } = useAuth();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [tier, setTier] = useState("all");
  const [status, setStatus] = useState("active");
  const [tagFilter, setTagFilter] = useState("all");
  const [allTags, setAllTags] = useState([]);
  const [sort, setSort] = useState("recent");
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [created, setCreated] = useState(null); // { email, temporary_password, welcome_email_sent } inline result
  const [copied, setCopied] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null); // single user row
  const [bulkDel, setBulkDel] = useState(false);
  const [confirmRestore, setConfirmRestore] = useState(null); // pending-deletion row
  const [purge, setPurge] = useState(null); // { eligible } preview, or null

  const ids = useMemo(() => (data?.users || []).map((u) => u.user_id), [data]);
  const sel = useRowSelection(ids);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { sort, page, page_size: 20 };
      if (q.trim()) params.q = q.trim();
      if (tier !== "all") params.tier = tier;
      if (status !== "all") params.status = status;
      if (tagFilter !== "all") params.tag = tagFilter;
      const { data } = await api.get("/admin/users", { params });
      setData(data);
    } catch { setData({ users: [], total: 0 }); }
    finally { setLoading(false); }
  }, [q, tier, status, tagFilter, sort, page]);

  // The member card's prev/next arrows walk this same filtered, sorted list.
  const rememberCtx = useCallback(
    () => saveMemberListCtx({ q: q.trim(), tier, status, tag: tagFilter, sort }),
    [q, tier, status, tagFilter, sort]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  useEffect(() => { api.get("/admin/account-tags").then(({ data }) => setAllTags(data)).catch(() => {}); }, []);
  // Reset to page 1 + clear selection when a filter changes (in the handlers — not an effect).
  const resetFilters = (apply) => { apply(); setPage(1); sel.clear(); };

  const patch = async (uid, body, msg) => {
    try { await api.patch(`/admin/users/${uid}`, body); toast.success(msg || "Updated"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };

  // Staff Messaging V1 — open (or reuse) this member's single official conversation and
  // jump to the shared Staff Inbox with it selected.
  const messageOfficially = async (uid) => {
    try {
      const { data: convo } = await api.post("/staff-messaging/conversations", { target_user_id: uid });
      navigate(`/dashboard/staff-inbox?c=${convo.id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not open the official conversation"); }
  };

  // Optimistic tag assignment (keeps the popover open across multiple toggles).
  const assignTags = async (uid, ids) => {
    setData((d) => d ? { ...d, users: d.users.map((u) => (u.user_id === uid ? { ...u, tags: ids } : u)) } : d);
    try { await api.patch(`/admin/users/${uid}`, { tags: ids }); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not update tags"); load(); }
  };

  const bulkTag = async (tagId, action) => {
    try {
      await api.post("/admin/account-tags/assign-bulk", { user_ids: sel.selected, [action]: [tagId] });
      toast.success(action === "add" ? "Tag added to selection" : "Tag removed from selection");
      sel.clear(); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Bulk update failed"); }
  };

  const toggleLicense = async (uid, enabled) => {
    try {
      await api.post(`/admin/users/${uid}/license/status`, { status: enabled ? "active" : "disabled" });
      toast.success(enabled ? "License enabled" : "License disabled");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update license"); }
  };

  const reissueLicense = async (uid) => {
    try { await api.post(`/admin/users/${uid}/license/reissue`); toast.success("New license key issued"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not reissue license"); }
  };

  const createUser = async () => {
    if (!form.first_name.trim() || !form.last_name.trim()) return toast.error("First and last name are required.");
    if (!form.email.trim()) return toast.error("Email is required.");
    if (!form.generate_password && form.password.length < 8) return toast.error("Password must be at least 8 characters.");
    setSaving(true);
    try {
      const body = {
        first_name: form.first_name.trim(), last_name: form.last_name.trim(),
        email: form.email.trim(), tier: form.tier, role: form.role,
      };
      if (form.generate_password) body.generate_password = true;
      else body.password = form.password;
      const { data } = await api.post("/admin/users", body);
      toast.success("Member created");
      load();
      if (data.temporary_password) {
        // Generated password — reveal ONCE inline; admin must copy/share it now.
        setCreated({ email: form.email.trim(), temporary_password: data.temporary_password, welcome_email_sent: data.welcome_email_sent });
        setCopied(false);
      } else {
        setShowAdd(false);
        setForm(EMPTY_FORM);
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create member"); }
    finally { setSaving(false); }
  };

  const closeAddDialog = () => {
    setShowAdd(false);
    setForm(EMPTY_FORM);
    setCreated(null);
    setCopied(false);
  };

  const copyTempPassword = async () => {
    try {
      await navigator.clipboard.writeText(created.temporary_password);
      setCopied(true);
      toast.success("Temporary password copied");
      setTimeout(() => setCopied(false), 2000);
    } catch { toast.error("Could not copy — select and copy it manually."); }
  };

  const removeUser = async (u) => {
    try {
      await api.delete(`/admin/users/${u.user_id}`);
      toast.success("Member moved to pending deletion");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not delete member"); }
    finally { setConfirmDelete(null); }
  };

  const restoreUser = async (u) => {
    try {
      await api.post(`/admin/users/${u.user_id}/restore`);
      toast.success("Account restored");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not restore account"); }
    finally { setConfirmRestore(null); }
  };

  const openPurge = async () => {
    try {
      const { data } = await api.post("/admin/users/purge/preview");
      setPurge(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not load purge preview"); }
  };

  const runPurge = async () => {
    try {
      const { data } = await api.post("/admin/users/purge/run");
      toast.success(`Purged ${data.purged} account${data.purged === 1 ? "" : "s"}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Purge failed"); }
    finally { setPurge(null); }
  };

  // ---- Bulk actions (skip self where unsafe; skip admins on delete) ----
  const bulkStatus = async (newStatus, verb) => {
    const targets = sel.selected.filter((uid) => newStatus === "active" || uid !== me?.user_id);
    if (targets.length === 0) return toast.error("Nothing to update (you can't suspend your own account).");
    await runBulk(targets, (uid) => api.patch(`/admin/users/${uid}`, { status: newStatus }), verb);
    sel.clear(); load();
  };
  const bulkSetTier = async (newTier) => {
    await runBulk(sel.selected, (uid) => api.patch(`/admin/users/${uid}`, { tier: newTier }), "Tier updated");
    sel.clear(); load();
  };
  const bulkDelete = async () => {
    const targets = (data?.users || []).filter((u) => sel.isSelected(u.user_id) && u.user_id !== me?.user_id && u.role !== "admin");
    const tIds = targets.map((u) => u.user_id);
    const skipped = sel.count - tIds.length;
    if (tIds.length === 0) { toast.error("Nothing to delete — selection is only you and/or admins."); setBulkDel(false); return; }
    await runBulk(tIds, (uid) => api.delete(`/admin/users/${uid}`), "Moved to pending deletion");
    if (skipped > 0) toast(`Skipped ${skipped} (you and/or other admins)`);
    sel.clear(); setBulkDel(false); load();
  };
  const deletableCount = useMemo(
    () => (data?.users || []).filter((u) => sel.isSelected(u.user_id) && u.user_id !== me?.user_id && u.role !== "admin").length,
    [data, sel, me],
  );

  const totalPages = data ? Math.max(1, Math.ceil(data.total / 20)) : 1;

  return (
    <div data-testid="admin-users">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-lg font-[640] text-foreground">Users & Tiers</h2>
        <AdminRefreshButton testId="admin-refresh-users" onRefresh={load} />
      </div>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Input value={q} onChange={(e) => resetFilters(() => setQ(e.target.value))} placeholder="Search by name, email or member ID…" data-testid="admin-users-search" />
        </div>
        <Select value={sort} onValueChange={(v) => resetFilters(() => setSort(v))}>
          <SelectTrigger className="w-full sm:w-44" data-testid="admin-users-sort"><SelectValue /></SelectTrigger>
          <SelectContent>{SORTS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={tier} onValueChange={(v) => resetFilters(() => setTier(v))}>
          <SelectTrigger className="w-full sm:w-40" data-testid="admin-users-tier-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All tiers</SelectItem>
            {TIERS.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={(v) => resetFilters(() => setStatus(v))}>
          <SelectTrigger className="w-full sm:w-40" data-testid="admin-users-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="suspended">Suspended</SelectItem>
            <SelectItem value="pending_deletion">Pending deletion</SelectItem>
            <SelectItem value="deleted">Deleted / purged</SelectItem>
          </SelectContent>
        </Select>
        <Select value={tagFilter} onValueChange={(v) => resetFilters(() => setTagFilter(v))}>
          <SelectTrigger className="w-full sm:w-40" data-testid="admin-users-tag-filter"><SelectValue placeholder="Tag" /></SelectTrigger>
          <SelectContent className="max-h-72">
            <SelectItem value="all">All tags</SelectItem>
            {allTags.filter((t) => t.active).map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button onClick={() => { setForm(EMPTY_FORM); setShowAdd(true); }} className="shrink-0" data-testid="admin-users-add">
          <UserPlus className="mr-2 h-4 w-4" /> Add member
        </Button>
        <Button variant="secondary" onClick={openPurge} className="shrink-0" data-testid="admin-users-purge" title="Hard-delete accounts past their 30-day recovery window">
          <Trash2 className="mr-2 h-4 w-4" /> Run purge
        </Button>
      </div>

      <Card className="mt-4 border-0 p-0">
        {loading && !data ? <div className="p-6"><Loading /></div> : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10"><Checkbox checked={sel.allSelected} onCheckedChange={sel.toggleAll} data-testid="admin-users-select-all" /></TableHead>
                  <TableHead>Member</TableHead>
                  <TableHead>Tier</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Tags</TableHead>
                  <TableHead>Joined</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>License</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(data?.users || []).map((u) => {
                  const isSelf = u.user_id === me?.user_id;
                  const isAdmin = u.role === "admin";
                  return (
                    <TableRow key={u.user_id} data-state={sel.isSelected(u.user_id) ? "selected" : undefined} data-testid={`admin-user-${u.user_id}`}>
                      <TableCell><Checkbox checked={sel.isSelected(u.user_id)} onCheckedChange={() => sel.toggle(u.user_id)} data-testid={`admin-user-select-${u.user_id}`} /></TableCell>
                      <TableCell>
                        <Link to={`/dashboard/admin/users/${u.user_id}`} onClick={rememberCtx} className="font-[560] text-foreground transition-colors hover:text-primary hover:underline" data-testid={`admin-user-name-${u.user_id}`}>
                          {[u.first_name, u.last_name].filter(Boolean).join(" ") || "—"}
                        </Link>
                        <div className="text-xs text-muted-foreground">{u.email}</div>
                        <div className="font-mono text-[11px] text-muted-foreground">{u.user_id}</div>
                      </TableCell>
                      <TableCell>
                        <Select value={u.tier} onValueChange={(v) => patch(u.user_id, { tier: v }, "Tier updated")}>
                          <SelectTrigger className="h-8 w-32" data-testid={`admin-user-tier-${u.user_id}`}><SelectValue /></SelectTrigger>
                          <SelectContent>{TIERS.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <Select value={u.role || "user"} onValueChange={(v) => patch(u.user_id, { role: v }, "Role updated")} disabled={isSelf}>
                          <SelectTrigger className="h-8 w-32" data-testid={`admin-user-role-${u.user_id}`}><SelectValue /></SelectTrigger>
                          <SelectContent>{ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell className="min-w-[12rem]">
                        <TagPicker allTags={allTags} value={u.tags || []} onChange={(ids) => assignTags(u.user_id, ids)} testId={`admin-user-tags-${u.user_id}`} compact max={1} />
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-sm text-muted-foreground" title={u.created_at ? new Date(u.created_at).toLocaleString() : "No signup date recorded"} data-testid={`admin-user-joined-${u.user_id}`}>
                        {fmtJoined(u.created_at)}
                      </TableCell>
                      <TableCell>
                        {u.status === "suspended"
                          ? <StatusBadge tone="danger">Suspended</StatusBadge>
                          : u.status === "pending_deletion"
                            ? <StatusBadge tone="warning" data-testid={`admin-user-pending-${u.user_id}`}>Pending · {u.deletion_source === "admin" ? "admin" : "self"}</StatusBadge>
                            : u.status === "deleted"
                              ? <StatusBadge tone="neutral">Purged</StatusBadge>
                              : <StatusBadge tone="neutral">Active</StatusBadge>}
                      </TableCell>
                      <TableCell>
                        <code className="block font-mono text-[11px] text-muted-foreground" data-testid={`admin-user-license-key-${u.user_id}`}>{u.license_key || "—"}</code>
                        <div className="mt-1 flex items-center gap-1.5">
                          <Switch checked={u.license_status !== "disabled"} onCheckedChange={(v) => toggleLicense(u.user_id, v)} data-testid={`admin-user-license-toggle-${u.user_id}`} />
                          <span className="text-[11px] text-muted-foreground" data-testid={`admin-user-license-status-${u.user_id}`}>{u.license_status === "disabled" ? "Disabled" : "Active"}</span>
                          <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => reissueLicense(u.user_id)} data-testid={`admin-user-license-reissue-${u.user_id}`} title="Reissue key"><RefreshCw className="h-3.5 w-3.5" /></Button>
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1.5">
                          <Link to={`/dashboard/admin/users/${u.user_id}`} onClick={rememberCtx}>
                            <Button size="sm" variant="ghost" data-testid={`admin-user-view-${u.user_id}`} title="Open member detail"><Eye className="h-4 w-4" /></Button>
                          </Link>
                          {/* Staff Messaging V1 — open this member's official Startup Otter thread. */}
                          {u.status === "active" && (
                            <Button size="sm" variant="ghost" className="text-primary" onClick={() => messageOfficially(u.user_id)}
                                    data-testid={`admin-user-message-${u.user_id}`} title="Message as Startup Otter"><BadgeCheck className="h-4 w-4" /></Button>
                          )}
                          {u.status === "pending_deletion" ? (
                            <Button size="sm" variant="ghost" className="text-emerald-600" onClick={() => setConfirmRestore(u)} data-testid={`admin-user-restore-${u.user_id}`} title="Restore account"><RotateCcw className="h-4 w-4" /></Button>
                          ) : u.status === "deleted" ? (
                            <span className="px-2 text-xs text-muted-foreground">Purged</span>
                          ) : (
                            <>
                              {u.status === "suspended" ? (
                                <Button size="sm" variant="ghost" onClick={() => patch(u.user_id, { status: "active" }, "Reactivated")} data-testid={`admin-user-reactivate-${u.user_id}`} title="Reactivate"><RotateCcw className="h-4 w-4" /></Button>
                              ) : (
                                <Button size="sm" variant="ghost" disabled={isSelf} className="text-destructive" onClick={() => patch(u.user_id, { status: "suspended" }, "Suspended")} data-testid={`admin-user-suspend-${u.user_id}`} title="Suspend"><Ban className="h-4 w-4" /></Button>
                              )}
                              <Button size="sm" variant="ghost" disabled={isSelf || isAdmin} className="text-destructive" onClick={() => setConfirmDelete(u)} data-testid={`admin-user-delete-${u.user_id}`} title={isAdmin ? "Demote this admin before deleting" : "Delete member"}><Trash2 className="h-4 w-4" /></Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {data && data.users.length === 0 && (
                  <TableRow><TableCell colSpan={9} className="py-10 text-center text-sm text-muted-foreground">No members match your filters.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>

      <div className="mt-3 flex items-center justify-between">
        <div className="text-sm text-muted-foreground">{data?.total ?? 0} members</div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="secondary" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="admin-users-prev"><ChevronLeft className="h-4 w-4" /></Button>
          <span className="text-sm text-muted-foreground">Page {page} / {totalPages}</span>
          <Button size="sm" variant="secondary" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} data-testid="admin-users-next"><ChevronRight className="h-4 w-4" /></Button>
        </div>
      </div>

      <BulkActionBar count={sel.count} onClear={sel.clear}>
        <Button size="sm" variant="secondary" onClick={() => bulkStatus("suspended", "Suspended")} data-testid="bulk-suspend"><Ban className="mr-1.5 h-4 w-4" /> Suspend</Button>
        <Button size="sm" variant="secondary" onClick={() => bulkStatus("active", "Reactivated")} data-testid="bulk-reactivate"><RotateCcw className="mr-1.5 h-4 w-4" /> Reactivate</Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild><Button size="sm" variant="secondary" data-testid="bulk-set-tier">Set tier</Button></DropdownMenuTrigger>
          <DropdownMenuContent>
            {TIERS.map((t) => <DropdownMenuItem key={t.value} onClick={() => bulkSetTier(t.value)} data-testid={`bulk-set-tier-${t.value}`}>{t.label}</DropdownMenuItem>)}
          </DropdownMenuContent>
        </DropdownMenu>
        <DropdownMenu>
          <DropdownMenuTrigger asChild><Button size="sm" variant="secondary" data-testid="bulk-add-tag">Add tag</Button></DropdownMenuTrigger>
          <DropdownMenuContent className="max-h-72 overflow-y-auto">
            {allTags.filter((t) => t.active).map((t) => <DropdownMenuItem key={t.id} onClick={() => bulkTag(t.id, "add")} data-testid={`bulk-add-tag-${t.key}`}>{t.name}</DropdownMenuItem>)}
            {allTags.filter((t) => t.active).length === 0 && <DropdownMenuItem disabled>No active tags</DropdownMenuItem>}
          </DropdownMenuContent>
        </DropdownMenu>
        <DropdownMenu>
          <DropdownMenuTrigger asChild><Button size="sm" variant="secondary" data-testid="bulk-remove-tag">Remove tag</Button></DropdownMenuTrigger>
          <DropdownMenuContent className="max-h-72 overflow-y-auto">
            {allTags.map((t) => <DropdownMenuItem key={t.id} onClick={() => bulkTag(t.id, "remove")} data-testid={`bulk-remove-tag-${t.key}`}>{t.name}</DropdownMenuItem>)}
          </DropdownMenuContent>
        </DropdownMenu>
        <Button size="sm" variant="ghost" className="text-destructive" onClick={() => setBulkDel(true)} data-testid="bulk-delete"><Trash2 className="mr-1.5 h-4 w-4" /> Delete</Button>
      </BulkActionBar>

      {/* Bulk delete confirm */}
      <AlertDialog open={bulkDel} onOpenChange={setBulkDel}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Move {deletableCount} member{deletableCount === 1 ? "" : "s"} to pending deletion?</AlertDialogTitle>
            <AlertDialogDescription>
              Each member moves into a 30-day pending-deletion window: inaccessible and hidden from public views, but fully preserved and restorable by an admin until the purge date. Your own account and any admins in the selection are skipped automatically.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={bulkDelete} data-testid="bulk-delete-confirm">Move to pending deletion</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Add member dialog */}
      <Dialog open={showAdd} onOpenChange={(o) => { if (!saving) { if (!o) closeAddDialog(); else setShowAdd(o); } }}>
        <DialogContent data-testid="admin-add-user-dialog">
          <DialogHeader>
            <DialogTitle>Add a member</DialogTitle>
            <DialogDescription>Create an account with a temporary password. The member is forced to set a new password at first sign-in. Share the temporary password directly — it is never emailed.</DialogDescription>
          </DialogHeader>
          {created ? (
            <div className="space-y-4" data-testid="admin-add-created-result">
              <div className="rounded-md border border-emerald-300 bg-emerald-50 p-3 text-sm text-emerald-900">
                Account created for <strong>{created.email}</strong>.
                {created.welcome_email_sent === false && " The welcome email could not be delivered (log mode / no key) — share the temporary password below."}
              </div>
              <div className="space-y-1.5">
                <Label>Temporary password (shown once)</Label>
                <div className="flex items-center gap-2">
                  <code className="flex-1 select-all rounded-md border bg-secondary/60 px-3 py-2 font-mono text-sm text-foreground" data-testid="admin-add-generated-password">
                    {created.temporary_password}
                  </code>
                  <Button type="button" variant="secondary" size="icon" onClick={copyTempPassword} data-testid="admin-add-copy-password" title="Copy">
                    {copied ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">This password won&apos;t be shown again and is never stored in plaintext. Copy it now and share it with the member securely.</p>
              </div>
            </div>
          ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>First name</Label>
              <Input maxLength={100} value={form.first_name} onChange={(e) => setForm((f) => ({ ...f, first_name: e.target.value.slice(0, 100) }))} data-testid="admin-add-first-name" />
            </div>
            <div className="space-y-1.5">
              <Label>Last name</Label>
              <Input maxLength={100} value={form.last_name} onChange={(e) => setForm((f) => ({ ...f, last_name: e.target.value.slice(0, 100) }))} data-testid="admin-add-last-name" />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Email</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} data-testid="admin-add-email" />
            </div>
            <div className="space-y-2 sm:col-span-2">
              <div className="flex items-center justify-between">
                <Label>Temporary password</Label>
                <div className="flex items-center gap-2">
                  <Switch checked={form.generate_password} onCheckedChange={(v) => setForm((f) => ({ ...f, generate_password: v, password: "" }))} data-testid="admin-add-generate-toggle" />
                  <span className="inline-flex items-center gap-1 text-xs text-muted-foreground"><Wand2 className="h-3.5 w-3.5" /> Generate a secure password</span>
                </div>
              </div>
              {form.generate_password ? (
                <p className="rounded-md border border-dashed border-border bg-secondary/40 px-3 py-2 text-xs text-muted-foreground" data-testid="admin-add-generate-note">
                  A strong random password will be generated and shown here once, after you create the member.
                </p>
              ) : (
                <Input type="text" value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} placeholder="At least 8 characters" data-testid="admin-add-password" />
              )}
            </div>
            <div className="space-y-1.5">
              <Label>Tier</Label>
              <Select value={form.tier} onValueChange={(v) => setForm((f) => ({ ...f, tier: v }))}>
                <SelectTrigger data-testid="admin-add-tier"><SelectValue /></SelectTrigger>
                <SelectContent>{TIERS.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm((f) => ({ ...f, role: v }))}>
                <SelectTrigger data-testid="admin-add-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">Member</SelectItem>
                  <SelectItem value="moderator">Moderator</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          )}
          <DialogFooter>
            {created ? (
              <Button onClick={closeAddDialog} data-testid="admin-add-done">Done</Button>
            ) : (
              <>
                <Button variant="secondary" onClick={closeAddDialog} disabled={saving}>Cancel</Button>
                <Button onClick={createUser} disabled={saving} data-testid="admin-add-submit">{saving ? "Creating…" : "Create member"}</Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Single delete confirm */}
      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Move {confirmDelete?.email} to pending deletion?</AlertDialogTitle>
            <AlertDialogDescription>
              This moves the member into a 30-day pending-deletion window. Their account becomes inaccessible and is hidden from public views, but nothing is destroyed — their profile, businesses, capital profiles, events, resources, handle and email are preserved and reserved. You can Restore them any time before the purge date.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={() => removeUser(confirmDelete)} data-testid="admin-user-delete-confirm">Move to pending deletion</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {/* Restore pending-deletion account */}
      <AlertDialog open={!!confirmRestore} onOpenChange={(o) => !o && setConfirmRestore(null)}>
        <AlertDialogContent data-testid="admin-restore-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Restore {confirmRestore?.email}?</AlertDialogTitle>
            <AlertDialogDescription>
              This cancels the pending deletion and brings the account back to active: their personal profile, businesses, capital profiles, events and resources are un-hidden (with their prior visibility), parked handles/slugs are reclaimed, and the license is re-activated. The user must log in again — no sessions are recreated.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => restoreUser(confirmRestore)} data-testid="admin-restore-confirm">Restore account</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Run eligible purge */}
      <AlertDialog open={!!purge} onOpenChange={(o) => !o && setPurge(null)}>
        <AlertDialogContent data-testid="admin-purge-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Run hard purge?</AlertDialogTitle>
            <AlertDialogDescription>
              {purge?.eligible ? (
                <>This permanently anonymizes <strong>{purge.eligible}</strong> account{purge.eligible === 1 ? "" : "s"} whose 30-day recovery window has passed: PII is scrubbed, emails are freed, and handles/slugs are released. This can&apos;t be undone.</>
              ) : (
                <>No accounts are past their 30-day recovery window yet — nothing to purge.</>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            {purge?.eligible > 0 && (
              <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={runPurge} data-testid="admin-purge-confirm">Purge {purge.eligible} account{purge.eligible === 1 ? "" : "s"}</AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
